// src/pages/tag/TagListPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTag, useI18n } from '@react/hooks';
import type { TagVisibility } from '@core/types/tag';
import type { Resource } from '@core/types/resource';
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  ErrorAlert,
} from '@react/components';

type VisibilityTab = 'all' | 'personal' | 'team' | 'tenant';

const VISIBILITY_MAP: Record<VisibilityTab, TagVisibility | undefined> = {
  all: undefined,
  personal: 'TAG_VISIBILITY_PERSONAL',
  team: 'TAG_VISIBILITY_TEAM',
  tenant: 'TAG_VISIBILITY_TENANT',
};

/**
 * Tags page — browse personal, team, and shared tags.
 * Click a tag to see all entities tagged with it.
 */
export const TagListPage = observer(() => {
  const store = useTag();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<VisibilityTab>(
    (searchParams.get('visibility') as VisibilityTab) || 'all',
  );
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [expandedTagId, setExpandedTagId] = useState<string | null>(null);
  const [taggedEntities, setTaggedEntities] = useState<Resource[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  // Fetch tags when tab or search changes
  useEffect(() => {
    const visibility = VISIBILITY_MAP[activeTab];
    const params: Record<string, string> = {};
    if (visibility) params.visibility = visibility;
    if (search) params.q = search;

    store.ListTags({
      pagination: {
        cursor: searchParams.get('cursor') || '',
        limit: 50,
        direction: 'forward',
      },
      sort: [],
      date_range: {} as any,
      filters: params as any,
    });
  }, [activeTab, search, searchParams.get('cursor')]);

  const handleTabChange = (tab: VisibilityTab) => {
    setActiveTab(tab);
    setExpandedTagId(null);
    setTaggedEntities([]);
    const params: Record<string, string> = {};
    if (tab !== 'all') params.visibility = tab;
    if (search) params.q = search;
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (activeTab !== 'all') params.visibility = activeTab;
    if (search) params.q = search;
    setSearchParams(params);
  };

  const handleTagClick = useCallback(async (tagId: string) => {
    if (expandedTagId === tagId) {
      setExpandedTagId(null);
      setTaggedEntities([]);
      return;
    }
    setExpandedTagId(tagId);
    setLoadingEntities(true);
    try {
      const result = await store.ListTaggedEntities(tagId);
      setTaggedEntities(result.resources?.items ?? []);
    } catch {
      setTaggedEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  }, [expandedTagId, store]);

  const visibilityLabel = (vis?: TagVisibility) => {
    switch (vis) {
      case 'TAG_VISIBILITY_PERSONAL': return t('tag.personal');
      case 'TAG_VISIBILITY_TEAM': return t('tag.team');
      case 'TAG_VISIBILITY_TENANT': return t('tag.tenant');
      default: return t('tag.tenant');
    }
  };

  const visibilityVariant = (vis?: TagVisibility): 'primary' | 'secondary' | 'warning' | 'success' => {
    switch (vis) {
      case 'TAG_VISIBILITY_PERSONAL': return 'primary';
      case 'TAG_VISIBILITY_TEAM': return 'warning';
      case 'TAG_VISIBILITY_TENANT': return 'secondary';
      default: return 'secondary';
    }
  };

  const tabs: { key: VisibilityTab; label: string }[] = [
    { key: 'all', label: t('tag.all') },
    { key: 'personal', label: t('tag.personal') },
    { key: 'team', label: t('tag.team') },
    { key: 'tenant', label: t('tag.tenant') },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('tag.myTags')}</h1>
        </div>
        <Link to="/tags/new">
          <Button>{t('tag.create')}</Button>
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
            <Button type="submit" variant="secondary">
              {t('common.search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Tag list */}
      {store.isLoading ? (
        <div className="flex items-center justify-center h-32">
          <span className="text-muted-foreground">{t('common.loading')}</span>
        </div>
      ) : store.items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t('tag.noItems')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {store.items.map((tag: any) => {
            const tagId = tag.entity?.id || tag.id;
            const isExpanded = expandedTagId === tagId;

            return (
              <Card key={tagId} className={isExpanded ? 'ring-1 ring-primary/30' : ''}>
                <div
                  className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleTagClick(tagId)}
                >
                  {/* Color dot */}
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tag.color || '#888' }}
                  />

                  {/* Icon */}
                  {tag.icon && (
                    <span className="text-base flex-shrink-0">{tag.icon}</span>
                  )}

                  {/* Name & description */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground truncate">{tag.name}</span>
                      <Badge variant={visibilityVariant(tag.visibility)} size="sm">
                        {visibilityLabel(tag.visibility)}
                      </Badge>
                    </div>
                    {tag.description && (
                      <p className="text-sm text-muted-foreground truncate mt-0.5">
                        {tag.description}
                      </p>
                    )}
                  </div>

                  {/* Usage count */}
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {t('tag.usageCount', { count: tag.usage_count || 0 })}
                  </span>

                  {/* Actions */}
                  <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/tags/${tagId}/edit`}>
                      <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                    </Link>
                  </div>

                  {/* Expand indicator */}
                  <svg
                    className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>

                {/* Expanded: tagged entities */}
                {isExpanded && (
                  <div className="border-t border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">{t('tag.taggedEntities')}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-4">
                      {loadingEntities ? (
                        <div className="text-sm text-muted-foreground py-4 text-center">
                          {t('common.loading')}
                        </div>
                      ) : taggedEntities.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4 text-center">
                          {t('tag.noTaggedEntities')}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {taggedEntities.map((entity) => (
                            <div
                              key={entity.urn}
                              className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors"
                            >
                              {/* Entity icon/color */}
                              {entity.icon ? (
                                <span className="text-base flex-shrink-0">{entity.icon}</span>
                              ) : (
                                <span
                                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: entity.color || '#888' }}
                                />
                              )}

                              {/* Entity info */}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-foreground truncate">
                                  {entity.title}
                                </div>
                                {entity.subtitle && (
                                  <div className="text-xs text-muted-foreground truncate">
                                    {entity.subtitle}
                                  </div>
                                )}
                              </div>

                              {/* Entity type */}
                              {entity.entity?.type_name && (
                                <Badge variant="secondary" size="sm">
                                  {entity.entity.type_name}
                                </Badge>
                              )}

                              {/* Status */}
                              {entity.status && (
                                <Badge variant="primary" size="sm">
                                  {entity.status}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default TagListPage;
